import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { TemplateList } from "@/components/template/TemplateList";
import { Plus, AlertCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useTemplateOperations } from "@/hooks/use-template-operations";
import { Template, ProcessingStatus } from "@/types/template";
import type { Database } from "@/integrations/supabase/types";

type TemplateRow = Database['public']['Tables']['templates']['Row'];

export default function Templates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { deleteTemplate } = useTemplateOperations();

  useEffect(() => {
    checkAdminStatus();
    fetchTemplates();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }

      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .single();

      setIsAdmin(roleData?.role === 'admin');
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Map the database response to our Template interface
      const mappedTemplates: Template[] = (data as TemplateRow[]).map(template => ({
        ...template,
        processing_status: template.processing_status as ProcessingStatus,
        processed_html: template.processed_html || null,
        placeholders: template.placeholders || null,
        letterhead_path: template.letterhead_path || null,
        is_company_library: template.is_company_library || false,
        metadata: template.metadata as Record<string, any> || {},
        chunk_progress: template.chunk_progress as Record<string, any> || {}
      }));
      
      setTemplates(mappedTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: "Error",
        description: "Failed to fetch templates. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (template: Template) => {
    try {
      console.log('Attempting to delete template:', template);
      await deleteTemplate(template);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: "Error",
        description: "Failed to delete template. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (template: Template) => {
    try {
      const { data, error } = await supabase.storage
        .from('templates')
        .download(template.file_path);

      if (error) throw error;

      // Create a download link
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = template.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast({
        title: "Error",
        description: "Failed to download template. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">My Templates</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTemplates}>
            Refresh
          </Button>
          <Button onClick={() => navigate("/templates/upload")}> 
            <Plus className="h-4 w-4 mr-2" />
            Add Template
          </Button>
        </div>
      </div>

      {templates.some(t => t.processing_status === 'pending') && (
        <Alert className="mb-6 bg-yellow-50 border-yellow-200">
          <AlertCircle className="h-4 w-4 text-yellow-500" />
          <AlertTitle>Templates Pending Review</AlertTitle>
          <AlertDescription>
            Some of your templates are pending review by an admin. You'll be able to use them once they're approved.
          </AlertDescription>
        </Alert>
      )}

      {templates.some(t => t.processing_status === 'failed') && (
        <Alert className="mb-6 bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <AlertTitle>Templates Failed</AlertTitle>
          <AlertDescription>
            Some of your templates failed to process. Please check the error messages and try uploading again.
          </AlertDescription>
        </Alert>
      )}

      <TemplateList
        templates={templates}
        onDelete={handleDelete}
        onDownload={handleDownload}
      />
    </div>
  );
}
